// 028C — Atención Niño-Niña de 5 a 9 Años (Serie 028, Fase 2, ver PLAN.md /
// tasks/investigacion-028-aiepi-denver.md). Cierra la corrección de
// numeración "028D mal puesto — debería ser 028C": el código bajo "028D"
// mezclaba el rango de 028B (2m-5a, con AIEPI) y 028C (5-9a, SIN AIEPI —
// confirmado con evidencia total contra la fuente primaria) en un
// Questionnaire genérico único. Acá se construye 028C con su contenido real
// (revisión de órganos y sistemas + examen físico regional/sistémico, sin
// AIEPI) y en el mismo PR se retira "028D" de todos los catálogos — recién
// acá, no antes, porque hasta este punto "028D" seguía siendo la única
// cobertura (rota pero funcional) para 5-9 años.
//
// Reusa el layout de "2 consultas por página" construido en la Tanda 8
// (028A) — mismo formato, contenido de página distinto. Pasa a Patrón B,
// mismo criterio que 028A/028B/038/033.
//
// Fuente primaria: páginas 149-150 del PDF oficial AM-00115-2021.
// "Datos del profesional responsable" (bloque K) no se captura acá: nombre,
// matrícula y firma los arma el servidor al firmar.
//
// Único contenido con recursos FHIR reales: cada diagnóstico no vacío crea
// un `Condition` — mismo patrón que 028A/028B/038.
import type { JSX } from 'react';
import { useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { ConsultaCard, GRID_DOS_CONSULTAS } from './DosConsultasLayout';
import { codeDeDiagnostico, diagnosticoConMarcaVacio, SelectorDiagnosticoConMarca, textoDiagnosticoConMarca, type DiagnosticoConMarca, type OpcionMarca } from './MarcaDiagnostico';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { constantesConImc, EXAMEN_FISICO_REGIONAL_028C, EXAMEN_FISICO_SISTEMICO_028C, REVISION_SISTEMAS_028C } from './bloques028Datos';

const CODIGO = '028C';
const TOPE_DIAGNOSTICOS = 5;

const REVISION_SISTEMAS = REVISION_SISTEMAS_028C;
const EXAMEN_FISICO_REGIONAL = EXAMEN_FISICO_REGIONAL_028C;
const EXAMEN_FISICO_SISTEMICO = EXAMEN_FISICO_SISTEMICO_028C;

const SI_NO_OPCIONES = ['si', 'no'] as const;

interface ConstantesVitales {
  temperatura: string;
  axilar: string;
  bucal: string;
  digital: string;
  pulso: string;
  frecuenciaRespiratoria: string;
  tensionArterial: string;
  peso: string;
  talla: string;
  imc: string;
  pulsioximetria: string;
}
const CONSTANTES_VACIAS: ConstantesVitales = {
  temperatura: '',
  axilar: '',
  bucal: '',
  digital: '',
  pulso: '',
  frecuenciaRespiratoria: '',
  tensionArterial: '',
  peso: '',
  talla: '',
  imc: '',
  pulsioximetria: '',
};

interface ConsultaAtencion5a9Anos {
  fecha: string;
  edad: string;
  constantes: ConstantesVitales;
  motivoConsulta: string;
  enfermedadActual: string;
  antecedentesPersonales: string;
  desarrolloPsicomotorAdecuado: string;
  riesgoSocial: string;
  antecedentesFamiliares: string;
  revisionSistemas: Record<string, boolean>;
  descripcionRevisionSistemas: string;
  examenFisicoRegional: Record<string, boolean>;
  examenFisicoSistemico: Record<string, boolean>;
  descripcionExamenFisico: string;
  diagnosticos: DiagnosticoConMarca[];
  planTratamiento: string;
}
function consultaVacia(): ConsultaAtencion5a9Anos {
  return {
    fecha: '',
    edad: '',
    constantes: { ...CONSTANTES_VACIAS },
    motivoConsulta: '',
    enfermedadActual: '',
    antecedentesPersonales: '',
    desarrolloPsicomotorAdecuado: '',
    riesgoSocial: '',
    antecedentesFamiliares: '',
    revisionSistemas: {},
    descripcionRevisionSistemas: '',
    examenFisicoRegional: {},
    examenFisicoSistemico: {},
    descripcionExamenFisico: '',
    diagnosticos: Array.from({ length: TOPE_DIAGNOSTICOS }, () => diagnosticoConMarcaVacio()),
    planTratamiento: '',
  };
}

function CampoTexto({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={label} etiqueta={label} valor={value} onChange={onChange} />;
}

export function Formulario028C({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f028c.titulo');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);

  const [consulta1, setConsulta1] = useState<ConsultaAtencion5a9Anos>(() => consultaVacia());
  const [consulta2, setConsulta2] = useState<ConsultaAtencion5a9Anos>(() => consultaVacia());

  const marcasDiagnostico: readonly OpcionMarca[] = [
    { valor: 'PRE', texto: t('formularios.f028c.marcaDiagnostico.pre') },
    { valor: 'DEF', texto: t('formularios.f028c.marcaDiagnostico.def') },
  ];

  function BloqueConsulta({ numero, value, onChange }: { numero: 1 | 2; value: ConsultaAtencion5a9Anos; onChange: (v: ConsultaAtencion5a9Anos) => void }): JSX.Element {
    return (
      <ConsultaCard titulo={t('formularios.f028c.consulta', { numero })}>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <Label>{t('formularios.f028c.fecha')}</Label>
            <Input type="date" value={value.fecha} onChange={(e) => onChange({ ...value, fecha: e.target.value })} />
          </div>
          <CampoTexto label={t('formularios.f028c.edad')} value={value.edad} onChange={(v) => onChange({ ...value, edad: v })} />
        </div>

        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028c.constantes.titulo')}</h5>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(Object.keys(CONSTANTES_VACIAS) as (keyof ConstantesVitales)[]).map((campo) => (
            <CampoTexto
              key={campo}
              label={t(`formularios.f028c.constantes.${campo}`)}
              value={value.constantes[campo]}
              onChange={(v) => onChange({ ...value, constantes: constantesConImc(value.constantes, campo, v) })}
            />
          ))}
        </div>

        <AreaDictado
          id={`f028c-consulta-${numero}-motivo`}
          etiqueta={t('formularios.f028c.motivoConsulta')}
          valor={value.motivoConsulta}
          onChange={(v) => onChange({ ...value, motivoConsulta: v })}
          filas={2}
        />
        <AreaDictado
          id={`f028c-consulta-${numero}-enfermedad-actual`}
          etiqueta={t('formularios.f028c.enfermedadActual')}
          valor={value.enfermedadActual}
          onChange={(v) => onChange({ ...value, enfermedadActual: v })}
          filas={2}
        />

        <AreaDictado
          id={`f028c-consulta-${numero}-antecedentes-personales`}
          etiqueta={t('formularios.f028c.antecedentesPersonales')}
          valor={value.antecedentesPersonales}
          onChange={(v) => onChange({ ...value, antecedentesPersonales: v })}
          filas={2}
        />
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1">
            <Label>{t('formularios.f028c.desarrolloPsicomotorAdecuado')}</Label>
            <Select value={value.desarrolloPsicomotorAdecuado || undefined} onValueChange={(v) => onChange({ ...value, desarrolloPsicomotorAdecuado: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SI_NO_OPCIONES.map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`formularios.f028c.siNoOpciones.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label>{t('formularios.f028c.riesgoSocial')}</Label>
            <Select value={value.riesgoSocial || undefined} onValueChange={(v) => onChange({ ...value, riesgoSocial: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SI_NO_OPCIONES.map((op) => (
                  <SelectItem key={op} value={op}>
                    {t(`formularios.f028c.siNoOpciones.${op}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <AreaDictado
          id={`f028c-consulta-${numero}-antecedentes-familiares`}
          etiqueta={t('formularios.f028c.antecedentesFamiliares')}
          valor={value.antecedentesFamiliares}
          onChange={(v) => onChange({ ...value, antecedentesFamiliares: v })}
          filas={2}
        />

        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028c.seccionRevisionSistemas')}</h5>
        <ChecklistGrid
          claves={REVISION_SISTEMAS}
          prefijoI18n="formularios.f028c.revisionSistemas"
          prefijoId={`revision-${numero}`}
          valores={value.revisionSistemas}
          onChange={(revisionSistemas) => onChange({ ...value, revisionSistemas })}
        />
        <AreaDictado
          id={`f028c-consulta-${numero}-descripcion-revision-sistemas`}
          etiqueta={t('formularios.f028c.descripcionRevisionSistemas')}
          valor={value.descripcionRevisionSistemas}
          onChange={(v) => onChange({ ...value, descripcionRevisionSistemas: v })}
          filas={2}
        />

        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028c.seccionExamenFisicoRegional')}</h5>
        <ChecklistGrid
          claves={EXAMEN_FISICO_REGIONAL}
          prefijoI18n="formularios.f028c.examenFisicoRegional"
          prefijoId={`regional-${numero}`}
          valores={value.examenFisicoRegional}
          onChange={(examenFisicoRegional) => onChange({ ...value, examenFisicoRegional })}
        />
        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028c.seccionExamenFisicoSistemico')}</h5>
        <ChecklistGrid
          claves={EXAMEN_FISICO_SISTEMICO}
          prefijoI18n="formularios.f028c.examenFisicoSistemico"
          prefijoId={`sistemico-${numero}`}
          valores={value.examenFisicoSistemico}
          onChange={(examenFisicoSistemico) => onChange({ ...value, examenFisicoSistemico })}
        />
        <AreaDictado
          id={`f028c-consulta-${numero}-descripcion-examen-fisico`}
          etiqueta={t('formularios.f028c.descripcionExamenFisico')}
          valor={value.descripcionExamenFisico}
          onChange={(v) => onChange({ ...value, descripcionExamenFisico: v })}
          filas={2}
        />

        <h5 className="text-sm font-medium text-hce-ink">{t('formularios.f028c.seccionDiagnostico')}</h5>
        <div className="flex flex-col gap-2">
          {value.diagnosticos.map((dx, i) => (
            <SelectorDiagnosticoConMarca
              key={i}
              label={`${t('formularios.f028c.diagnostico')} ${i + 1}`}
              marcaLabel={t('formularios.f028c.marcaDiagnostico.label')}
              marcas={marcasDiagnostico}
              value={dx}
              onChange={(nuevo) => onChange({ ...value, diagnosticos: value.diagnosticos.map((d, idx) => (idx === i ? nuevo : d)) })}
            />
          ))}
        </div>

        <AreaDictado
          id={`f028c-consulta-${numero}-plan-tratamiento`}
          etiqueta={t('formularios.f028c.planTratamiento')}
          valor={value.planTratamiento}
          onChange={(v) => onChange({ ...value, planTratamiento: v })}
          filas={2}
        />
      </ConsultaCard>
    );
  }

  function paresDeConsulta(consulta: ConsultaAtencion5a9Anos, numero: 1 | 2): Par[] {
    const prefijo = t('formularios.f028c.consulta', { numero });
    return [
      { pregunta: `${prefijo} — ${t('formularios.f028c.fecha')}`, respuesta: consulta.fecha },
      { pregunta: `${prefijo} — ${t('formularios.f028c.edad')}`, respuesta: consulta.edad },
      {
        pregunta: `${prefijo} — ${t('formularios.f028c.constantes.titulo')}`,
        respuesta: (Object.keys(CONSTANTES_VACIAS) as (keyof ConstantesVitales)[])
          .filter((c) => consulta.constantes[c])
          .map((c) => `${t(`formularios.f028c.constantes.${c}`)}: ${consulta.constantes[c]}`)
          .join(' | '),
      },
      { pregunta: `${prefijo} — ${t('formularios.f028c.motivoConsulta')}`, respuesta: consulta.motivoConsulta.trim() },
      { pregunta: `${prefijo} — ${t('formularios.f028c.enfermedadActual')}`, respuesta: consulta.enfermedadActual.trim() },
      { pregunta: `${prefijo} — ${t('formularios.f028c.antecedentesPersonales')}`, respuesta: consulta.antecedentesPersonales.trim() },
      {
        pregunta: `${prefijo} — ${t('formularios.f028c.desarrolloPsicomotorAdecuado')}`,
        respuesta: consulta.desarrolloPsicomotorAdecuado ? t(`formularios.f028c.siNoOpciones.${consulta.desarrolloPsicomotorAdecuado}`) : '',
      },
      { pregunta: `${prefijo} — ${t('formularios.f028c.riesgoSocial')}`, respuesta: consulta.riesgoSocial ? t(`formularios.f028c.siNoOpciones.${consulta.riesgoSocial}`) : '' },
      { pregunta: `${prefijo} — ${t('formularios.f028c.antecedentesFamiliares')}`, respuesta: consulta.antecedentesFamiliares.trim() },
      {
        pregunta: `${prefijo} — ${t('formularios.f028c.seccionRevisionSistemas')}`,
        respuesta: REVISION_SISTEMAS.filter((c) => consulta.revisionSistemas[c]).map((c) => t(`formularios.f028c.revisionSistemas.${c}`)).join(', '),
      },
      { pregunta: `${prefijo} — ${t('formularios.f028c.descripcionRevisionSistemas')}`, respuesta: consulta.descripcionRevisionSistemas.trim() },
      {
        pregunta: `${prefijo} — ${t('formularios.f028c.seccionExamenFisicoRegional')}`,
        respuesta: EXAMEN_FISICO_REGIONAL.filter((c) => consulta.examenFisicoRegional[c]).map((c) => t(`formularios.f028c.examenFisicoRegional.${c}`)).join(', '),
      },
      {
        pregunta: `${prefijo} — ${t('formularios.f028c.seccionExamenFisicoSistemico')}`,
        respuesta: EXAMEN_FISICO_SISTEMICO.filter((c) => consulta.examenFisicoSistemico[c]).map((c) => t(`formularios.f028c.examenFisicoSistemico.${c}`)).join(', '),
      },
      { pregunta: `${prefijo} — ${t('formularios.f028c.descripcionExamenFisico')}`, respuesta: consulta.descripcionExamenFisico.trim() },
      ...consulta.diagnosticos
        .map((dx, i) => ({ pregunta: `${prefijo} — ${t('formularios.f028c.diagnostico')} ${i + 1}`, respuesta: textoDiagnosticoConMarca(dx, marcasDiagnostico) }))
        .filter((p) => p.respuesta.trim() !== ''),
      { pregunta: `${prefijo} — ${t('formularios.f028c.planTratamiento')}`, respuesta: consulta.planTratamiento.trim() },
    ].filter((p) => p.respuesta.trim() !== '');
  }

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = [...paresDeConsulta(consulta1, 1), ...paresDeConsulta(consulta2, 2)];

      const lote = new LoteTransaccional();
      for (const consulta of [consulta1, consulta2]) {
        for (const dx of consulta.diagnosticos) {
          if (dx.diagnostico.texto.trim()) {
            lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(dx.diagnostico), recordedDate: new Date().toISOString() });
          }
        }
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <div className={GRID_DOS_CONSULTAS}>
        <BloqueConsulta numero={1} value={consulta1} onChange={setConsulta1} />
        <BloqueConsulta numero={2} value={consulta2} onChange={setConsulta2} />
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
